import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Verbindung, type Verbindungslage } from '../kopplung/verbindung'
import { verbindungSetzen, verbindungBeenden } from '../kopplung/aktiv'
import { Maskottchen } from '../spiel/Maskottchen'
import { Sterne } from '../spiel/Deko'
import { tonAufwecken } from '../ton'

/**
 * Das Tablet tritt dem Fernseher bei.
 *
 * Der Code kommt entweder aus dem QR-Code (dann steht er in der Adresse und
 * wird sofort genutzt) oder wird von Hand eingetippt. Vier Ziffern, mehr
 * nicht: Ein Achtjaehriges soll das allein hinbekommen.
 */
export function Koppeln() {
  const [parameter] = useSearchParams()
  const navigate = useNavigate()
  const [code, setCode] = useState(parameter.get('code') ?? '')
  const [lage, setLage] = useState<Verbindungslage | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const verbindung = useRef<Verbindung | null>(null)
  /**
   * Ob die Kopplung geklappt hat, als Ref statt als Zustand.
   *
   * Die Aufraeumfunktion eines Effekts sieht immer die Werte von dem Render,
   * in dem sie erzeugt wurde. Eine Zustandsvariable waere dort also noch die
   * alte, und die gerade erst zustande gekommene Verbindung wuerde sofort
   * wieder gekappt. Ein Ref ist zum Aufraeumzeitpunkt aktuell.
   */
  const gekoppelt = useRef(false)

  function verbinden(eingegeben: string) {
    if (eingegeben.length !== 4) {
      setFehler('Der Code hat vier Ziffern.')
      return
    }
    tonAufwecken()
    setFehler(null)
    setLage('verbinde')

    const v = new Verbindung({
      onLage: (neu, grund) => {
        setLage(neu)
        if (neu === 'gekoppelt') {
          gekoppelt.current = true
          verbindungSetzen(v)
          navigate('/uebung')
        }
        if (neu === 'fehler') {
          setFehler(fehlertext(grund))
          v.schliessen()
        }
        if (neu === 'getrennt' && grund === 'tv-weg') {
          setFehler('Der Fernseher hat die Verbindung beendet.')
        }
      },
      onStandAngefordert: () => {
        // Der Fernseher wurde neu geladen. Die Uebungsseite haengt an diesem
        // Ereignis und schickt ihren aktuellen Stand noch einmal hinueber.
        window.dispatchEvent(new Event('zahlenflug:stand-anfordern'))
      },
    })
    verbindung.current = v
    v.alsTablet(eingegeben)
  }

  // Kam der Code per QR-Code mit, sofort verbinden. Sonst muesste das Kind
  // nach dem Scannen noch einmal auf einen Knopf tippen, ohne Grund.
  useEffect(() => {
    const ausAdresse = parameter.get('code')
    if (!ausAdresse || ausAdresse.length !== 4) return
    // Der Zustand der Verbindung selbst ist die Bedingung, kein eigenes
    // Merkflag. Im StrictMode haengt React jeden Effekt einmal aus und wieder
    // ein; ein Flag waere danach gesetzt, die Verbindung aber geschlossen,
    // und es käme nie wieder eine zustande.
    if (verbindung.current) return
    verbinden(ausAdresse)

    return () => {
      // Nur aufraeumen, wenn die Kopplung nicht zustande kam. Eine fertige
      // Verbindung wird auf der Uebungsseite gebraucht und lebt im Modul weiter.
      if (!gekoppelt.current) {
        verbindung.current?.schliessen()
        verbindung.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameter])

  useEffect(() => {
    return () => {
      if (!gekoppelt.current) verbindungBeenden()
    }
  }, [])

  return (
    <div className="buehne">
      <Sterne />
      <div className="mitte">
        <Maskottchen stimmung={fehler ? 'trost' : 'staunt'} gross />
        <h1 className="titel">Mit dem Fernseher verbinden</h1>
        <p className="unterzeile">Tippe die vier Zahlen ein, die auf dem Fernseher stehen.</p>

        <input
          className="code-eingabe"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0000"
          autoFocus
          aria-label="Kopplungscode"
        />

        {fehler && <p className="fehler">{fehler}</p>}
        {lage === 'verbinde' && <p className="rueck__weiter">Verbinde …</p>}

        <div className="zeile" style={{ marginTop: '4vmin' }}>
          <button className="knopf" onClick={() => verbinden(code)} type="button">
            Verbinden
          </button>
          <button className="knopf knopf--leise" onClick={() => navigate('/')} type="button">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}

function fehlertext(grund?: string): string {
  if (grund === 'unbekannter-code') return 'Den Code gibt es nicht. Schau nochmal auf den Fernseher.'
  if (grund === 'schon-belegt') return 'Hier spielt schon jemand.'
  if (grund === 'kein-websocket') return 'Dieser Browser kann keine Verbindung aufbauen.'
  return 'Die Verbindung hat nicht geklappt. Versuch es nochmal.'
}
